import { useEffect } from "react";
import {
  trackScoreBookingClick,
  trackScoreBookingComplete,
  type ScoreBookingSource,
} from "@/lib/analytics";
import type { ScoreNextIntent, ScoreSourcePlacement } from "@/lib/scoreJourney";

type BookingContext = {
  placement: ScoreSourcePlacement;
  nextIntent: ScoreNextIntent;
  bookingSource: ScoreBookingSource;
  pageType?: "service";
  niche?: "ai_citation_programme";
  leadId?: number;
};

const CALENDLY_URL = "https://calendly.com/modielnadi/30min";

export function ScoreBookingCompletionListener() {
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== "https://calendly.com") return;
      if (event.data?.event === "calendly.event_scheduled") {
        trackScoreBookingComplete();
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);
  return null;
}

/**
 * Native anchor behavior remains available for assistive technology, blocked
 * scripts, and failed popup initialization. The optional popup is progressive
 * enhancement only; it never opens automatically.
 */
export function ScoreBookingLink({
  context,
  children,
  className,
  ariaLabel,
  id,
}: {
  context: BookingContext;
  children: React.ReactNode;
  className?: string;
  ariaLabel?: string;
  id?: string;
}) {
  const eventContext = {
    placement: context.placement,
    next_intent: context.nextIntent,
    booking_source: context.bookingSource,
    ...(context.pageType ? { page_type: context.pageType } : {}),
    ...(context.niche ? { niche: context.niche } : {}),
    ...(context.leadId !== undefined ? { lead_id: context.leadId } : {}),
  };

  return (
      <a
        href={CALENDLY_URL}
        target="_blank"
        rel="noopener"
        aria-label={ariaLabel}
        id={id}
        className={className}
        data-booking-source={context.bookingSource}
        onClick={(event) => {
          trackScoreBookingClick(eventContext);
          if (!window.Calendly?.initPopupWidget) return;
          event.preventDefault();
          window.Calendly.initPopupWidget({ url: CALENDLY_URL });
        }}
      >
        {children}
      </a>
  );
}
